import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { RootState, AppDispatch } from '../app/store'
import { logError } from '../utils/logger'
import { fetchOrderDetails, setSelectedOrder } from '../features/orders/ordersSlice'
import OrderConfirmationLayout from '../components/orderConfirmation/OrderConfirmationLayout'
import LoadingSpinner from '../components/LoadingSpinner'

const OrderConfirmationPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const dispatch = useDispatch<AppDispatch>()
  
  const { settings } = useSelector((state: RootState) => state.settings)
  const { user } = useSelector((state: RootState) => state.auth)
  const { selectedOrder, isLoading } = useSelector((state: RootState) => state.orders)
  const isCustomerServiceUser =
    user?.type === 'customer_service' || user?.user_type === 'customer_service'
  
  const [orderId, setOrderId] = useState<string | number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [shouldAutoPrint, setShouldAutoPrint] = useState(false)
  const [hasAutoPrinted, setHasAutoPrinted] = useState(false)
  
  const isRTL = settings.language === 'ar'

  useEffect(() => {
    // Get orderId and orderData from multiple sources: location state, URL params, or selectedOrder
    let idToUse: string | number | null = null
    let orderDataFromState: any = null
    
    // Try location state first
    const stateData = (location.state as any)
    const stateOrderId = stateData?.orderId
    const stateOrderData = stateData?.orderData
    const stateShouldAutoPrint = stateData?.shouldAutoPrint || false
    
    // Set auto print flag
    if (stateShouldAutoPrint) {
      setShouldAutoPrint(true)
    }
    
    if (stateOrderId) {
      idToUse = stateOrderId
    }
    
    if (stateOrderData) {
      orderDataFromState = stateOrderData
      // Try to extract orderId from orderData if not already set
      if (!idToUse && orderDataFromState) {
        idToUse = orderDataFromState.id || 
                  orderDataFromState.order_id || 
                  orderDataFromState.orderId
      }
    }
    
    // Try URL params
    if (!idToUse && params.orderId) {
      idToUse = params.orderId
    }
    
    // Try selectedOrder from Redux (in case it was already fetched)
    if (!idToUse && selectedOrder?.id) {
      idToUse = selectedOrder.id
    }
    
    if (idToUse) {
      setOrderId(idToUse)
      setIsFetching(true)
      setError(null)
      
      // Always fetch order details to ensure data is properly mapped
      // This ensures nested objects (branch, customer, address) are properly handled
      // Only skip if we already have the correct order in Redux
      if (selectedOrder && selectedOrder.id?.toString() === idToUse.toString()) {
        // Order already in Redux and matches, no need to fetch
        setIsFetching(false)
      } else {
        // Fetch order details to get properly mapped data
        dispatch(fetchOrderDetails(idToUse) as any)
          .then((result: any) => {
            setIsFetching(false)
            if (fetchOrderDetails.rejected.match(result)) {
              // If fetch fails but we have orderData from state, use it as fallback
              // But only if it looks like it's already been mapped (has proper structure)
              if (orderDataFromState && typeof orderDataFromState === 'object') {
                // Check if orderData looks like it needs mapping (has nested objects that might cause issues)
                const needsMapping = orderDataFromState.branch && typeof orderDataFromState.branch === 'object' && !orderDataFromState.branch.name
                if (!needsMapping) {
                  // Data seems safe to use
                  dispatch(setSelectedOrder(orderDataFromState))
                  setError(null)
                } else {
                  // Data needs mapping but we can't do it here, show error
                  setError('Failed to fetch order details')
                }
              } else {
                setError('Failed to fetch order details')
              }
            } else {
              setError(null)
            }
          })
          .catch((err: any) => {
            setIsFetching(false)
            // If fetch fails but we have orderData from state, use it as fallback
            if (orderDataFromState && typeof orderDataFromState === 'object') {
              // Check if orderData looks like it needs mapping
              const needsMapping = orderDataFromState.branch && typeof orderDataFromState.branch === 'object' && !orderDataFromState.branch.name
              if (!needsMapping) {
                dispatch(setSelectedOrder(orderDataFromState))
                setError(null)
              } else {
                setError('Failed to fetch order details')
              }
            } else {
              setError('Failed to fetch order details')
            }
          })
      }
    } else {
      setError('Order ID not found')
      setIsFetching(false)
    }
  }, [location, params, dispatch, selectedOrder])

  // Auto-print receipt immediately after order submission (skip for CS users)
  useEffect(() => {
    if (shouldAutoPrint && !hasAutoPrinted && selectedOrder && !isFetching && !isLoading && !isCustomerServiceUser) {
      // Wait a short time to ensure the receipt is rendered in the DOM
      const printTimeout = setTimeout(() => {
        try {
          // Find the receipt container
          const receiptContainer = document.querySelector('.receipt-container') as HTMLElement
          
          if (receiptContainer) {
            // Trigger print dialog quickly
            setTimeout(() => {
              window.print()
              setHasAutoPrinted(true)
            }, 50)
          } else {
            // If receipt container not found, try to print anyway after a short delay
            setTimeout(() => {
              window.print()
              setHasAutoPrinted(true)
            }, 200)
          }
        } catch (err: any) {
          logError('Auto print error:', err)
          setHasAutoPrinted(true) // Mark as printed even if error to avoid retrying
        }
      }, 200) // Reduced wait time for faster printing
      
      return () => clearTimeout(printTimeout)
    }
  }, [shouldAutoPrint, hasAutoPrinted, selectedOrder, isFetching, isLoading])

  const handleNewOrder = () => {
    navigate('/home')
  }

  const handlePrint = () => {
    if (!selectedOrder) {
      return
    }

    try {
      // Wait a bit to ensure the receipt is rendered in the DOM
      setTimeout(() => {
        // Find the receipt container
        const receiptContainer = document.querySelector('.receipt-container') as HTMLElement
        
        if (receiptContainer) {
          // Just trigger print - CSS will handle showing the receipt in print mode
          // No need to change classes, @media print will handle visibility
          setTimeout(() => {
            // Trigger print dialog
            window.print()
          }, 100)
        } else {
          // If receipt container not found, try to print anyway
          // The receipt might be rendered elsewhere or the print will show what's visible
          window.print()
        }
      }, 200)
    } catch (err: any) {
      logError('Print error:', err)
    }
  }

  // Show loading if fetching or if order is being loaded or if we have orderId but no order yet
  if (isFetching || isLoading || (orderId && !selectedOrder && !error)) {
    return <LoadingSpinner />
  }

  // Show error if there's an error or no order found
  if (error || (!selectedOrder && orderId)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || 'Order not found'}</p>
          <button
            onClick={() => navigate('/home')}
            className="px-4 py-2 bg-flutter-button text-white rounded-lg"
          >
            {t('common.back_to_home') || 'العودة للرئيسية'}
          </button>
        </div>
      </div>
    )
  }

  // If no orderId at all, show error
  if (!orderId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-500 mb-4">Order ID not found</p>
          <button
            onClick={() => navigate('/home')}
            className="px-4 py-2 bg-flutter-button text-white rounded-lg"
          >
            {t('common.back_to_home') || 'العودة للرئيسية'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <OrderConfirmationLayout
      order={selectedOrder}
      onNewOrder={handleNewOrder}
      onPrint={handlePrint}
      isRTL={isRTL}
      showPrintButton={!isCustomerServiceUser}
      isCustomerService={isCustomerServiceUser}
    />
  )
}

export default OrderConfirmationPage
